import { translate } from '@/translation/translate.ts';
import { FC, KeyboardEvent, useCallback, useId, useRef } from 'react';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';

interface EditSaveButtonsProps {
  editMode: boolean;
  isValid: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
}

export const EditSaveButtons: FC<EditSaveButtonsProps> = ({ editMode, isValid, onSave, onStartEdit, onCancelEdit }) => {
  const { eventConfig } = useEventConfig();
  const s = eventConfig!.slides;
  const saveHintId = useId();

  const handleKeyActivate = (cb: () => void) => (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      cb();
    }
  };

  const groupRef = useRef<HTMLDivElement>(null);

  const onContainerKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
      return;
    }

    const container = groupRef.current;
    if (!container) return;

    const buttons = Array.from(container.querySelectorAll<HTMLButtonElement>('button.edit-button')).filter(
      (b) => !b.disabled
    );

    if (buttons.length === 0) return;

    const active = document.activeElement as HTMLElement | null;
    const currentIndex = buttons.findIndex((b) => b === active || b.contains(active as Node));

    let nextIndex = currentIndex;
    const backwards = e.key === 'ArrowLeft' || e.key === 'ArrowUp';

    if (backwards) {
      nextIndex = currentIndex <= 0 ? buttons.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex === -1 || currentIndex >= buttons.length - 1 ? 0 : currentIndex + 1;
    }

    e.preventDefault();
    buttons[nextIndex]?.focus();
  }, []);

  return (
    <div className={'edit-button-group'} ref={groupRef} onKeyDown={onContainerKeyDown}>
      {editMode && (
        <>
          <button
            type="button"
            className={'edit-button'}
            data-cy={'cancel-button'}
            onClick={onCancelEdit}
            onKeyDown={handleKeyActivate(onCancelEdit)}
          >
            {translate(s.dataSummary.cancelLabel)}
          </button>

          <button
            type="button"
            disabled={!isValid}
            aria-disabled={!isValid}
            className={'edit-button'}
            title={!isValid ? translate(s.dataSummary.requiredMessage!) : ''}
            data-cy={'save-button'}
            onClick={onSave}
            onKeyDown={handleKeyActivate(onSave)}
          >
            {translate(s.dataSummary.saveLabel)}
          </button>
        </>
      )}

      {!editMode && (
        <button
          type="button"
          className={'edit-button'}
          data-cy={'edit-button'}
          onClick={onStartEdit}
          onKeyDown={handleKeyActivate(onStartEdit)}
        >
          {translate(s.dataSummary.editLabel)}
        </button>
      )}
    </div>
  );
};
